import { makeAutoObservable, observe, runInAction } from "mobx";
import { createClient, localeToShort } from "@/core/helpers";
import { ApiClient } from "@/clients/ApiClient";

type PodcastBundle = { podcast: Podcast; episodes: Episode[]; count: number };

export class PodcastStore {
    podcasts: Podcast[] = [];
    podcast: Podcast | null = null;
    episodes: Episode[] = [];
    episode: Episode | null | undefined = null;
    count = 0;
    page = 1;
    limit = 30;
    loading = false;
    loadingEpisodesList = false;
    loadingSimilar = false;
    loadingSeasons = false;
    loadingSeason = false;
    latestLoaded: { limit: number; lang: string } | null = null;
    next: Episode | null = null;
    previous: Episode | null = null;
    similar: Podcast[] = [];
    stats: SiteStats | null = null;
    private client: ApiClient;
    seasons: SeasonsResponse = {
        seasons: [],
    };
    season: Season | null = null;
    private loadedSeasonsForPodcastId: number | null = null;
    private loadedSeasonId: number | null = null;

    constructor() {
        makeAutoObservable(this);
        this.client = createClient();
        // oxlint-disable-next-line typescript/no-explicit-any
        observe(this, (change: any) => {
            if (change.name === "podcast") {
                this.seasons = {
                    seasons: [],
                };
                this.season = null;
                this.loadedSeasonsForPodcastId = null;
                this.loadedSeasonId = null;
            }
        });
    }

    /**
     * Loads a podcast and its episode page. State is committed in one go once everything
     * has arrived, so a page that is still on screen (during a view transition) never sees half-loaded data.
     */
    async getPodcastBySlug(slug: string, page = 1) {
        if (this.podcast && this.podcast.slug === slug) {
            if (page !== this.page) {
                const { episodes, count } = await this.fetchEpisodes(this.podcast.id, page);
                runInAction(() => {
                    this.episodes = episodes;
                    this.count = count;
                    this.page = page;
                });
            }
            return;
        }

        const bundle = await this.fetchPodcastBundle(slug, page);
        this.applyPodcastBundle(bundle, page);
    }

    private async fetchEpisodes(id: number, page: number) {
        const { episodes, count } = await this.client.get<EpisodesResponse>(`/podcasts/${id}/episodes?page=${page}`);
        return { episodes, count: count || 0 };
    }

    private async fetchPodcastBundle(slug: string, page: number) {
        const { podcast } = await this.client.get<SinglePodcastResponse>(`/podcasts/bySlug/${slug}`);
        const { episodes, count } = await this.fetchEpisodes(podcast.id, page);
        return { podcast, episodes, count };
    }

    /** Warms the API cache for a podcast page so navigating to it needs no network wait. */
    prefetchPodcastBySlug(slug: string) {
        if (this.podcast?.slug === slug) {
            return;
        }
        this.fetchPodcastBundle(slug, 1).catch(() => undefined);
    }

    private applyPodcastBundle({ podcast, episodes, count }: PodcastBundle, page: number) {
        runInAction(() => {
            this.podcast = podcast;
            this.episodes = episodes;
            this.count = count;
            this.page = page;
        });
    }

    async getPodcast(id: number) {
        const { podcast } = await this.client.get<SinglePodcastResponse>(`/podcasts/${id}`);
        this.podcast = podcast;
    }

    async getPodcastFull(id: number) {
        const { podcast } = await this.client.get<SinglePodcastResponse>(`/podcasts/${id}`);
        const { episodes } = await this.client.get<EpisodesResponse>(`/podcasts/${id}/episodes`);
        this.podcast = podcast;
        this.episodes = episodes;
    }

    async getEpisodeBySlug(slug: string, blogSlug: string) {
        let podcast = this.podcast;
        let bundle: PodcastBundle | null = null;

        if (!podcast || podcast.slug !== blogSlug) {
            bundle = await this.fetchPodcastBundle(blogSlug, 1);
            podcast = bundle.podcast;
        }

        const { episode, next, previous } = await this.client.get<SingleEpisodeResponse>(
            `/podcasts/${podcast.id}/episodes/${slug}`
        );

        runInAction(() => {
            if (bundle) {
                this.applyPodcastBundle(bundle, 1);
            }
            this.episode = episode;
            this.next = next || null;
            this.previous = previous || null;
        });
    }

    async getEpisodes(id: number, page = 1) {
        this.loadingEpisodesList = true;
        this.episodes = [];
        this.page = page;
        const { episodes, count } = await this.client.get<EpisodesResponse>(`/podcasts/${id}/episodes?page=${page}`);
        this.count = count || 0;
        this.episodes = episodes;
        this.loadingEpisodesList = false;
    }

    async loadSimilar(podcast: Podcast) {
        this.loadingSimilar = true;
        this.similar = [];
        const lang = podcast.language ? localeToShort(podcast.language) : "sv";
        const { podcasts } = await this.client.get<PodcastsByCountResponse>(
            `/podcasts/${podcast.id}/similar?lang=${lang}&count=12`
        );

        const entries = Object.entries(podcasts);
        this.similar = entries
            .sort(() => 0.5 - Math.random())
            .map((item) => {
                return item[1].podcast;
            });
        this.loadingSimilar = false;
    }

    async loadStats() {
        const { stats } = await this.client.get<StatsResponse>("/podcasts/stats");
        this.stats = stats;
    }

    async loadLatest(limit = 30, lang = "sv") {
        const { podcasts } = await this.client.get<PodcastsResponse>(
            `/podcasts?sort=created_at&dir=desc&limit=${limit}&pod=1&lang=${lang}`
        );
        this.podcasts = podcasts;
        this.latestLoaded = { limit, lang };
    }

    /** True when the podcast list already holds the newest `limit` podcasts for `lang` (or more). */
    hasLatest(limit: number, lang: string) {
        return this.latestLoaded?.lang === lang && this.latestLoaded.limit >= limit;
    }

    async loadSeasons() {
        if (!this.podcast) return;
        this.seasons = await this.client.get<SeasonsResponse>(`/podcasts/${this.podcast.id}/seasons`);
    }

    async loadSeason(seasonId: number) {
        if (!this.podcast) return;
        this.season = await this.client.get<SeasonResponse>(`/podcasts/${this.podcast.id}/seasons/${seasonId}`);
    }

    clearSimilar() {
        this.similar = [];
    }

    /**
     * Auto-loads podcast by slug if not already loaded.
     * Safe to call multiple times - only fetches once per slug.
     */
    async ensurePodcastBySlug(slug: string, page = 1) {
        if (this.podcast?.slug === slug) {
            if (page !== this.page) {
                try {
                    await this.getEpisodes(this.podcast.id, page);
                } catch (error) {
                    console.error("Failed to load episodes:", error);
                    this.episodes = [];
                    this.loadingEpisodesList = false;
                }
            }
            return;
        }
        this.loading = true;
        try {
            await this.getPodcastBySlug(slug, page);
        } catch (error) {
            console.error("Failed to load podcast by slug:", error);
            this.podcast = null;
            this.episodes = [];
        } finally {
            this.loading = false;
        }
    }

    /** True when the podcast and season for these params are already loaded. */
    hasSeason(slug: string, seasonId: number) {
        return this.podcast?.slug === slug && this.loadedSeasonId === seasonId && !!this.season;
    }

    /**
     * Loads a podcast and one of its seasons, committing both in one go once everything has arrived,
     * so a page that is still on screen never sees a half-loaded season.
     */
    async openSeason(slug: string, seasonId: number) {
        let podcast = this.podcast;
        let bundle: PodcastBundle | null = null;

        if (!podcast || podcast.slug !== slug) {
            bundle = await this.fetchPodcastBundle(slug, 1);
            podcast = bundle.podcast;
        }

        const season = await this.client.get<SeasonResponse>(`/podcasts/${podcast.id}/seasons/${seasonId}`);

        runInAction(() => {
            if (bundle) {
                this.applyPodcastBundle(bundle, 1);
            }
            this.season = season;
            this.loadedSeasonId = seasonId;
        });
    }

    /**
     * Auto-loads seasons for the current podcast if not already loaded.
     * Safe to call multiple times - only fetches once per podcast.
     */
    async ensureSeasons() {
        if (!this.podcast) return;
        if (this.loadedSeasonsForPodcastId === this.podcast.id) return;

        this.loadingSeasons = true;
        try {
            this.seasons = await this.client.get<SeasonsResponse>(`/podcasts/${this.podcast.id}/seasons`);
            this.loadedSeasonsForPodcastId = this.podcast.id;
        } catch (error) {
            console.error("Failed to load seasons:", error);
            this.seasons = { seasons: [] };
            // Mark as attempted to prevent infinite retries
            this.loadedSeasonsForPodcastId = this.podcast.id;
        } finally {
            this.loadingSeasons = false;
        }
    }

    /**
     * Auto-loads a specific season if not already loaded.
     * Safe to call multiple times - only fetches once per season.
     */
    async ensureSeason(seasonId: number) {
        if (!this.podcast) return;
        if (this.loadedSeasonId === seasonId) return;

        this.loadingSeason = true;
        try {
            this.season = await this.client.get<SeasonResponse>(`/podcasts/${this.podcast.id}/seasons/${seasonId}`);
            this.loadedSeasonId = seasonId;
        } catch (error) {
            console.error("Failed to load season:", error);
            this.season = null;
            // Mark as attempted to prevent infinite retries
            this.loadedSeasonId = seasonId;
        } finally {
            this.loadingSeason = false;
        }
    }

    get popularPodcasts(): Podcast[] {
        if (!this.stats) return [];
        return this.stats.popular.podcasts.podcasts.slice(0, 5);
    }

    get popularEpisodes(): Episode[] {
        if (!this.stats) return [];
        return this.stats.popular.episodes.episodes.slice(0, 5);
    }
}
